import { View, Pressable } from 'react-native';
import { ReactNode, useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import Modal from 'shared/components/lazy-modal.web';
import { Props } from 'shared/@types';
import { ModalHeader } from 'shared/components/modal-header';
import { Text } from 'shared/components/text';
import { lockScroll, unlockScroll } from 'shared/library/scroll-lock';

export type BaseModalProps = Props<{
  trigger?: (modal: BaseModalRef) => ReactNode;
  onClose?: () => void;
  closeOnBackdropPress?: boolean;
  withBackdrop?: boolean;
  surface?: 'cream' | 'white';
}>;

export type BaseModalRef = {
  open: () => void;
  close: () => void;
};

export const BaseModal = forwardRef<
  BaseModalRef,
  BaseModalProps & { title?: string; description?: ReactNode; footer?: ReactNode }
>(
  (
    {
      trigger,
      title,
      description,
      footer,
      closeOnBackdropPress = true,
      withBackdrop = true,
      surface = 'cream',
      onClose,
      children,
    },
    ref,
  ) => {
    const [isMounted, setIsMounted] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const modalHasOpened = useRef(false);
    const surfaceClassName = surface === 'white' ? 'bg-white' : 'bg-brand-cream-light';

    function open() {
      setIsMounted(true);
      setShowModal(true);
    }

    function close() {
      onClose?.();
      setShowModal(false);
      // A pending lazy import has no exit animation or onModalHide callback.
      if (!modalHasOpened.current) setIsMounted(false);
    }

    useEffect(() => {
      if (!showModal) return;

      lockScroll();
      return unlockScroll;
    }, [showModal]);

    const modalRef: BaseModalRef = {
      open,
      close,
    };

    useImperativeHandle(ref, () => modalRef);

    return (
      <>
        {trigger?.(modalRef)}

        {isMounted && (
          <Modal
            isVisible={showModal}
            onLoadingCancel={close}
            animationIn="fadeIn"
            animationOut="fadeOut"
            hideModalContentWhileAnimating={true}
            onBackButtonPress={() => closeOnBackdropPress && close()}
            onBackdropPress={() => closeOnBackdropPress && close()}
            onModalWillShow={() => {
              modalHasOpened.current = true;
            }}
            onModalHide={() => {
              modalHasOpened.current = false;
              setIsMounted(false);
            }}
            backdropOpacity={1}
            customBackdrop={
              <Pressable
                onPress={() => closeOnBackdropPress && close()}
                className={
                  'bg-brand-brown-dark/70 fixed inset-0 h-screen w-screen cursor-default outline-none'
                }
                testID={'modal-backdrop'}
              />
            }
            style={{ margin: 0 }}
          >
            {withBackdrop ? (
              <View className="mx-auto max-h-dvh w-full max-w-lg p-3">
                <View
                  className={
                    'shadow-brand-brown-dark/20 max-h-[calc(100dvh-1.5rem)] overflow-hidden rounded-lg shadow-lg ' +
                    surfaceClassName
                  }
                >
                  <ModalHeader title={title} onClose={close} />

                  <View className={'min-h-0 shrink overflow-y-auto overflow-x-hidden p-5'}>
                    {!!description && (
                      <Text
                        className={
                          children ? 'text-brand-brown-dark mb-6' : 'text-brand-brown-dark'
                        }
                      >
                        {description}
                      </Text>
                    )}
                    {children}
                  </View>

                  {!!footer && (
                    <View className={'border-brand-brown-dark/10 shrink-0 border-t px-5 py-3'}>
                      <View
                        pointerEvents={'none'}
                        aria-hidden={true}
                        className={
                          'from-brand-cream-light/0 to-brand-cream-light absolute inset-x-0 bottom-full mb-px h-6 bg-gradient-to-b'
                        }
                      />

                      {footer}
                    </View>
                  )}
                </View>
              </View>
            ) : (
              children
            )}
          </Modal>
        )}
      </>
    );
  },
);

BaseModal.displayName = 'BaseModal';
